import { Dispatch, SetStateAction } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { PageChanger } from "@/components/PageChanger";
import { cn } from "@/lib/utils";
import type { IterationOutcomes } from "@/generated";
import {
  failedIterations,
  iterationCounts,
} from "@/components/Spotflow/WorkflowRunner/Outputs/v2FanOut";

/** "N succeeded · N failed · N skipped · N not run" for a fan-out that
 *  failed; skipped and not run are shown only when non-zero. */
export function FanOutIterationCounts({
  totalIterations,
  outcomes,
  className,
}: {
  totalIterations: number;
  outcomes: IterationOutcomes;
  className?: string;
}) {
  const counts = iterationCounts(totalIterations, outcomes);
  return (
    <span
      className={cn("whitespace-nowrap text-muted-foreground", className)}
      data-testid="fanout-iteration-counts"
    >
      {counts.succeeded} succeeded ·{" "}
      <span className="text-destructive">{counts.failed} failed</span>
      {counts.skipped > 0 && <> · {counts.skipped} skipped</>}
      {counts.notRun > 0 && <> · {counts.notRun} not run</>}
    </span>
  );
}

/** Iteration pager for a V2 fan-out node's results. Given the `outcomes` of
 *  a fan-out that failed, it marks the failed pages, jumps to the next failed
 *  one and (unless the caller places them elsewhere) shows the counts. */
export function FanOutIterationPager({
  page,
  setPage,
  totalPages,
  outcomes,
  showCounts = true,
  size = "sm",
  className,
}: {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  outcomes?: IterationOutcomes | null;
  showCounts?: boolean;
  size?: "md" | "sm" | "xs";
  className?: string;
}) {
  const failed = failedIterations(outcomes);

  const jumpToNextFailed = () => {
    if (failed.length === 0) return;
    setPage(failed.find((it) => it > page) ?? failed[0]);
  };
  const jumpLabel =
    failed.length === 1
      ? "See the failed iteration"
      : "See the next failed iteration";

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {outcomes && showCounts && (
        <FanOutIterationCounts
          totalIterations={totalPages}
          outcomes={outcomes}
          className="text-xs"
        />
      )}
      {failed.length > 0 && (
        <NoxusTooltip content={jumpLabel} placement="bottom" asChild>
          <Button
            variant="ghostDestructive"
            size="iconxs"
            aria-label={jumpLabel}
            onClick={jumpToNextFailed}
          >
            <AlertTriangle className="size-4" />
          </Button>
        </NoxusTooltip>
      )}
      <PageChanger
        currentPage={page}
        setCurrentPage={setPage}
        totalPages={totalPages}
        maxWidth="3rem"
        size={size}
        erroredPages={failed}
      />
    </div>
  );
}
